import React, { FunctionComponent } from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import moment from 'moment';
import styled, { useTheme } from 'styled-components';

// Store
import AppState from '@webapp/store/state';
import { UIMenus } from '@webapp/store/ui/types';
import { getFullName } from '@webapp/store/user/selectors';
import { CloseMenu } from '@webapp/store/ui/action-creators';

// Types
import User from '@common/types/user/user';
import { EntitySubscription } from '@common/types/subscription-manager/list-subscriptions-response';

// Components
import NewMenu from '@components/new-menu';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import UserAvatarInfo from '@components/basics/user-avatar-info';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';

interface SuccessSubscriptionMenuProps extends Partial<User> {
  className?: string;
  type: 'merch' | 'distribution' | 'artist';
  artistSubs?: EntitySubscription;
  clientId: string;
  isMobile: boolean;
  openedMenu?: UIMenus;
  closeMenu: () => CloseMenu;
  upgradeArtistCount?: number;
}

const mapStateToProps = (state: AppState) => {
  const clientId = state.artist.active?.id as string;
  return {
    clientId,
    artistSubs: clientId
      ? state.subscriptionManager.summary?.artists.find(
          (a) => a.id === clientId
        )
      : undefined,
    avatar: state.user.profile?.avatar,
    fullName: getFullName(state),
    isMobile: state.ui.mobile,
    openedMenu: state.ui.menuOpened,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
});

const SuccessSubscriptionMenu: FunctionComponent<
  SuccessSubscriptionMenuProps
> = ({
  className,
  avatar,
  fullName,
  type,
  artistSubs,
  closeMenu,
  openedMenu,
  isMobile,
}) => {
  React.useEffect(() => {
    // If the user successfully subscribes to a Pro plan, then show a success message for 10 seconds.
    if (
      (openedMenu === 'SUCCESS_SUBSCRIPTION_MERCH_PRO' ||
        openedMenu === 'SUCCESS_SUBSCRIPTION_DIST_PRO') &&
      avatar
    ) {
      let timer = setTimeout(() => closeMenu(), 5000);
      return () => clearTimeout(timer);
    }
  }, [openedMenu, avatar, closeMenu]);

  const theme = useTheme();

  const getTitle = () => {
    switch (type) {
      case 'merch':
        return 'USER_PROFILE.SUBSCRIPTION.SUCCESS_MENU.MERCH_PRO';
      case 'distribution':
        return 'USER_PROFILE.UPGRADE_USER_DESCRIPTION';
      case 'artist':
        return 'USER_PROFILE.SUBSCRIPTION.SUCCESS_MENU.DIST_PRO';
      default:
        return '';
    }
  };

  const getMenuId = () => {
    switch (type) {
      case 'merch':
        return 'SUCCESS_SUBSCRIPTION_MERCH_PRO';
      case 'distribution':
      case 'artist':
        return 'SUCCESS_SUBSCRIPTION_DIST_PRO';

      default:
        return '';
    }
  };
  const getExpirationDate = () => {
    const planType =
      type === 'distribution' || type === 'artist' ? 'distribution' : type;
    return artistSubs?.subscriptions?.find((s) => s.type === planType)
      ?.nextBilling;
  };

  return (
    <NewMenu
      className={className}
      noButton
      menuId={getMenuId()}
      centeredPadding={!isMobile}
      topHeader={
        isMobile && avatar && <UserAvatarInfo src={avatar} name={fullName!} />
      }
      header={
        !isMobile && avatar && <UserAvatarInfo src={avatar} name={fullName!} />
      }
      footer={
        <Typography align="center" color={theme.colors.tertiary}>
          <Translate id="SUCCESS.REDIRECT" />
        </Typography>
      }
    >
      <Content>
        <Flex
          className="mb-20"
          justifyContent="space-between"
          alignItems="center"
        >
          <CompletedIcon className="icon" />
          <Typography
            className="date"
            align="right"
            type="small"
            color={theme.colors.tertiary}
          >
            {moment(new Date()).format('DD MMM YYYY')}
          </Typography>
        </Flex>
        <Typography className="mb-20" type="subheader" customFont="semi-bold">
          <Translate id={getTitle()} />
        </Typography>
        {!(type === 'distribution') ? (
          <Typography type="small" color={theme.colors.tertiary}>
            <Translate
              id="USER_PROFILE.SUBSCRIPTION.SUCCESS_MENU.ACTIVE_UNTIL"
              data={{ date: moment(getExpirationDate()).format('DD MMM YYYY') }}
            />
          </Typography>
        ) : (
          <Typography type="small" color={theme.colors.tertiary}></Typography>
        )}
      </Content>
    </NewMenu>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SuccessSubscriptionMenu);

const Content = styled(Flex)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;

  .icon {
    width: 32px;
    height: 32px;
  }
`;
